import { levenshteinEditDistance } from 'levenshtein-edit-distance';

import { FormControl, FormGroup, Validators } from '@angular/forms';

import { QuestionStateService } from '@core/services';

import { fractionInputValidator } from '@shared/validators';

import { MatchedData, NestedQuestion, Question, QuestionField, QuestionType, ResponseData, TaxResponseGroup } from './models';

type MatchWithDataIncluded = {
    declarant?: TaxResponseGroup;
    decData?: ResponseData;
    partner?: TaxResponseGroup;
    partData?: ResponseData;
};

const RELATIVE_DISTANCE_CUTOFF = 0.5;

export function emptyStringAsUndefined(input?: string): string | undefined {
    return input?.trim() == '' || input === undefined ? undefined : input;
}

export function matchGroups(
    question: NestedQuestion,
    accountantData?: TaxResponseGroup[],
    declarantData?: TaxResponseGroup[],
    partnerData?: TaxResponseGroup[],
): MatchedData[] {
    if (!question.multi) {
        return [{ accountant: accountantData?.[0], declarant: declarantData?.[0], partner: partnerData?.[0] }];
    }
    const matchedEndUserData = getMatchedEndUserData(declarantData, partnerData);
    const result = matchAccountantToEndUsersData(question, accountantData ?? [], matchedEndUserData);
    // Sort to have the result appear in the same order as in silverfin
    result.sort((a, b) => (a.accountant?.index ?? Infinity) - (b.accountant?.index ?? Infinity));
    return result;
}

export function generateActivations(
    fields: QuestionField[],
    forms: FormGroup[],
    external?: boolean,
    state?: QuestionStateService,
): Record<number, boolean> {
    let result = {} as Record<number, boolean>;
    fields
        .filter((field) => field.conditions !== undefined)
        .forEach((field) => {
            let active = false;
            forms.forEach((form) => {
                active = active || field.isActivatedBy(form, fields, external ?? false, state);
            });
            result[field.id] = active;
        });
    return result;
}

export function generateControl(field: QuestionField): FormControl {
    let control: FormControl;
    if ([QuestionType.Check, QuestionType.Radio].includes(field.type)) {
        control = new FormControl<boolean>(false);
    } else if (field.type === QuestionType.Number) {
        control = new FormControl<number | null>(null);
    } else if ([QuestionType.File, QuestionType.SingleFile].includes(field.type)) {
        control = new FormControl<null | string[]>(null);
    } else if (field.type === QuestionType.Email) {
        control = new FormControl<string | null>(null, { validators: [Validators.email] });
    } else if (field.type === QuestionType.Telephone) {
        control = new FormControl<string | null>(null, { validators: [Validators.pattern('^\\+?1?[0-9]{8,15}$')] });
    } else if (field.type === QuestionType.Fraction) {
        control = new FormControl<string | null>(null, { validators: [fractionInputValidator()] });
    } else {
        control = new FormControl<string | null>(null);
    }
    if (field.required && field.conditions === undefined) {
        control.addValidators([Validators.required]);
    }
    if (field.readOnly) {
        control.disable();
    }
    return control;
}

export function cleanFieldData(data: any, field?: QuestionField): any {
    if (data === null || field === undefined) {
        return data;
    }
    switch (field.type) {
        case QuestionType.Check:
        case QuestionType.Radio:
            return data === 'true' || data === 1 || data === true;
        case QuestionType.Date:
            //Convert to iso from dd/mm/yyyy
            if (typeof data === 'string') {
                const convTypeMatch = data.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                if (convTypeMatch !== null) {
                    return `${convTypeMatch[4]}-${convTypeMatch[3]}-${convTypeMatch[2]}`;
                }
            }
            return data;
        case QuestionType.Number:
        case QuestionType.Integer:
        case QuestionType.Currency:
        case QuestionType.Percentage:
            const num = Number(data);
            if (!isNaN(num)) {
                return field.type === QuestionType.Percentage ? num * 100 : num;
            }
            return data;
        case QuestionType.Fraction:
            return getDisplayFraction(data);
        case QuestionType.Select:
            return (field.options?.length ?? 0 > 0) && field.options![0].values.includes(data) ? String(data) : null;
        case QuestionType.Telephone:
        case QuestionType.Text:
        case QuestionType.Url:
            return String(data);
        default:
            return data;
    }
}

export function fillForm(
    question: NestedQuestion,
    form: FormGroup,
    group: TaxResponseGroup,
    external?: boolean,
    state?: QuestionStateService,
): void {
    Object.keys(form.controls).forEach((key) => {
        let control = form.get(key);
        const field = question.inputFields?.find((field) => field.tag === key);
        const rec = group.records.find((rec) => rec.inputField == field?.id);
        if (control instanceof FormControl) {
            let value: any;
            if ([QuestionType.Radio, QuestionType.Check].includes(field?.type ?? QuestionType.Text)) {
                value = rec?.data ?? false;
            } else if ([QuestionType.File, QuestionType.SingleFile].includes(field?.type ?? QuestionType.Text)) {
                value = (rec?.documents ?? []).map((doc) => doc.uuid);
            } else if (field?.type == QuestionType.SelectDependent) {
                value = rec?.dependsOnGroup;
            } else {
                value = rec?.data ?? null;
            }
            control.setValue(cleanFieldData(value, field));
        }
    });
    let oldForm: any = undefined;
    while (JSON.stringify(form.getRawValue()) !== oldForm) {
        oldForm = JSON.stringify(form.getRawValue());
        question.inputFields
            ?.filter((f) => f.type === QuestionType.HiddenField)
            .forEach((inputField) => {
                form.get(inputField.tag)?.setValue(inputField.isActivatedBy(form, question.inputFields ?? [], external ?? false, state));
            });
    }

    if (!external) {
        return;
    }

    // Inject final hidden values in external state service
    question.inputFields
        ?.filter((f) => f.type === QuestionType.HiddenField)
        .forEach((inputField) => {
            state?.addFieldToExternalState(question.id, group.id, inputField.id, form.get(inputField.tag)?.value);
        });
}

function getMatchedEndUserData(declarantGroups?: TaxResponseGroup[], partnerGroups?: TaxResponseGroup[]): MatchWithDataIncluded[] {
    if (declarantGroups?.[0] === undefined && partnerGroups?.[0] === undefined) {
        return [];
    }

    const declarantData = declarantGroups?.map((group) => group.toDataMap()) ?? [];
    const partnerData = partnerGroups?.map((group) => group.toDataMap()) ?? [];

    if (partnerData.length == 0 && declarantData.length !== 0) {
        return declarantGroups!.map((group, idx) => {
            return { declarant: group, decData: declarantData[idx] };
        });
    }
    if (declarantData.length === 0 && partnerData.length !== 0) {
        return partnerGroups!.map((group, idx) => {
            return { partner: group, partData: partnerData[idx] };
        });
    }

    let distanceData = [] as { 'declId': number; 'partId': number; 'dist': number }[];
    let results = [] as MatchWithDataIncluded[];

    //Greedy matching
    // calculate all distances
    let matchedDeclIdx = new Set<number>();
    let matchedPartIdx = new Set<number>();
    declarantData.forEach((data: ResponseData, idx: number) => {
        partnerData.forEach((partData: ResponseData, jdx: number) => {
            distanceData.push({ declId: idx, partId: jdx, dist: getRelativeDataDistance(data, partData) });
        });
    });
    // Eliminate all rows and columns above the threshold
    distanceData = distanceData.filter((res) => res.dist < RELATIVE_DISTANCE_CUTOFF);
    distanceData.sort((res1, res2) => res1.dist - res2.dist);
    // Perform best-match first matching
    while (distanceData.length > 0) {
        const bestMatch = distanceData[0];
        matchedDeclIdx.add(bestMatch.declId);
        matchedPartIdx.add(bestMatch.partId);
        results.push({
            declarant: declarantGroups![bestMatch.declId],
            decData: declarantData[bestMatch.declId],
            partner: partnerGroups![bestMatch.partId],
            partData: partnerData[bestMatch.partId],
        });
        // Filter out new impossible matches
        distanceData = distanceData.filter((data) => data.declId !== bestMatch.declId && data.partId !== bestMatch.partId);
    }

    // Handle non-matched data
    [...Array(declarantData.length).keys()]
        .filter((val) => !matchedDeclIdx.has(val))
        .forEach((declIdx) => {
            results.push({ declarant: declarantGroups![declIdx], decData: declarantData[declIdx] });
        });
    [...Array(partnerData.length).keys()]
        .filter((val) => !matchedPartIdx.has(val))
        .forEach((partIdx) => {
            results.push({ partner: partnerGroups![partIdx], partData: partnerData[partIdx] });
        });

    return results;
}

function matchAccountantToEndUsersData(
    question: NestedQuestion,
    accountantGroups: TaxResponseGroup[],
    externalMatches: MatchWithDataIncluded[],
): MatchedData[] {
    if (externalMatches.length === 0) {
        return accountantGroups.map((group) => {
            return { accountant: group };
        });
    }

    // Generate initial results with only the external data
    let fieldIdsToMatch = question.inputFields?.filter((f) => f.syncableToSilverfin).map((f) => f.id) ?? [];
    let result = externalMatches.map((match) => {
        return { partner: match.partner, declarant: match.declarant };
    }) as MatchedData[];
    if (accountantGroups.length === 0) {
        return result;
    }
    const accountantData = accountantGroups.map((group) => group.toDataMap());
    const potentialMatches = [...Array(accountantData.length).keys()];
    const resultIndices = [...Array(result.length).keys()];

    //Check each matched user data pair and see if any accountant data matches it well enough
    // This ensures the best fit - no other accountant data matches it better
    for (const resIdx of resultIndices) {
        const item = externalMatches[resIdx];
        const minDistances = potentialMatches.map((m) => {
            let res = 0;
            let cnt = 0;

            if (item.decData !== undefined) {
                res += getRelativeDataDistance(accountantData[m], item.decData, fieldIdsToMatch);
                cnt += 1;
            }
            if (item.partData !== undefined) {
                res += getRelativeDataDistance(accountantData[m], item.partData, fieldIdsToMatch);
                cnt += 1;
            }
            return res / cnt;
        });
        const index = minDistances.indexOf(Math.min(...minDistances));
        const minDist = minDistances[index];
        if (minDist <= RELATIVE_DISTANCE_CUTOFF) {
            result[resIdx].accountant = accountantGroups[potentialMatches[index]];
            potentialMatches.splice(index, 1);
        }
    }
    //The remaining non-matched accountant data is added without external data
    potentialMatches.forEach((idx) => result.push({ accountant: accountantGroups[idx] }));

    return result;
}

function getRelativeDataDistance(data1: ResponseData, data2: ResponseData, idsToMatch?: number[]): number {
    const keys1 = Object.keys(data1);
    const keys2 = Object.keys(data2);
    const commonKeys = [...keys1].filter((x) => keys2.includes(x) && (idsToMatch?.includes(Number(x)) ?? true));
    const diffKeys = [
        ...keys1.filter((x) => !keys2.includes(x) && (idsToMatch?.includes(Number(x)) ?? true)),
        ...keys2.filter((x) => !keys1.includes(x) && (idsToMatch?.includes(Number(x)) ?? true)),
    ];
    if (commonKeys.length === 0 && diffKeys.length === 0) {
        return 0;
    }
    let distance = diffKeys.length; //Start distance is the number of fields they don't share
    for (const key of commonKeys) {
        //Case 1: different types
        if (typeof data1[key] != typeof data2[key]) {
            distance++;
        } else if (typeof data1[key] !== 'string' && !Array.isArray(data1[key])) {
            //Case 2: compare values
            distance = distance + (data1[key] == data2[key] ? 0 : 1);
            continue;
        } else if (Array.isArray(data1[key])) {
            //Case 3: skip file lists (uuids are different)
            continue;
        } else if (typeof data1[key] == 'string') {
            //Case 4: Levensthein distance for strings
            const d1 = data1[key] as string;
            const d2 = data2[key] as string;
            distance += levenshteinEditDistance(d1, d2, true) / Math.max(d1.length, d2.length);
        } else {
            //Unknown, should not happen
            distance += 1;
        }
    }
    return distance / (commonKeys.length + diffKeys.length);
}

// Accepts strings in percentage, decimal and fraction.
// Returns the simplified fraction representation
export function exportFraction(fracValStr: string): string {
    let workVal = fracValStr.replace(' ', '').replace(',', '.');
    if (workVal.includes('%')) {
        //Percentage
        workVal = (Number(workVal.slice(0, workVal.length - 1)) / 100).toFixed(4);
    }
    if (!workVal.includes('/') && !workVal.includes('.')) {
        // Numerical int
        workVal += '/1';
    }
    if (workVal.includes('.')) {
        const numDecimals = workVal.length - workVal.lastIndexOf('.') - 1;
        workVal = `${workVal.replace('.', '')}/${Math.pow(10, numDecimals)}`;
    }
    const simplified = simplifyFraction(workVal);
    return `${simplified.numerator}/${simplified.denominator}`;
}

type Fraction = {
    numerator: number;
    denominator: number;
};

function getDisplayFraction(fracStr: string): string {
    if (!fracStr.includes('/')) {
        console.debug('invalid fraction', fracStr);
        return '0/1';
    }
    const frac = simplifyFraction(fracStr);
    if (frac.denominator == 1) {
        return `${(100 * frac.numerator).toFixed(2)}%`;
    }
    return `${frac.numerator}/${frac.denominator}`;
}

function simplifyFraction(fracStr: string): Fraction {
    const trimmed = fracStr.replace(' ', '').split('/');
    const emptyFraction = { numerator: 0, denominator: 1 } as Fraction;
    if (trimmed.length != 2) {
        console.debug('invalid fraction', fracStr);
        return emptyFraction;
    }
    let numerator = Number(trimmed[0]);
    let denominator = Number(trimmed[1]);
    if (isNaN(numerator) || isNaN(denominator) || denominator == 0) {
        console.debug('invalid fraction', fracStr);
        return emptyFraction;
    }
    const divider = gcd(numerator, denominator);
    numerator /= divider;
    denominator /= divider;
    return { numerator, denominator } as Fraction;
}

function gcd(a: number, b: number): number {
    if (b === 0) {
        return a;
    }
    return gcd(b, a % b);
}
